import { Box } from '@mui/material';
import React from 'react';
import { GlobalConfiguration } from '../../../models/GlobalConfiguration';
import StyledFieldDescription from '../../styles/StyledFieldDescription';
import { StyledInput, StyledSwitch } from '../../styles/StyledInputs';
import StyledSection from '../../styles/StyledSection';
import { StyledFieldRow, StyledHeading, StyledLabel } from '../../styles/styles';
import ConfigFieldLabel from './ConfigFieldLabel';
import GuardrailMessageEditor from './GuardrailMessageEditor';
import { GLOBAL_CONFIG_FIELD_HELP, GLOBAL_CONFIG_FIELD_LABELS } from './globalConfigFieldConfig';

type VeraResponsesSectionProps = {
  config: GlobalConfiguration;
  dirtyFields: Set<string>;
  onChange: (field: string, value: unknown) => void;
};

type GroundingToggleRowProps = {
  fieldKey: 'enableVeraGoogleSearchGrounding' | 'enableVeraGlobalChatSearchLinks';
  dirtyFields: Set<string>;
  description: React.ReactNode;
  checked: boolean;
  disabled?: boolean;
  onChange: (field: string, value: unknown) => void;
};

/** Switch + label + description row for the two Vera search-grounding toggles. */
function GroundingToggleRow({
  fieldKey,
  dirtyFields,
  description,
  checked,
  disabled,
  onChange,
}: GroundingToggleRowProps) {
  return (
    <StyledFieldRow unrestrictedWidth sx={{ mb: 2 }} data-field={fieldKey}>
      <StyledSwitch
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(fieldKey, e.target.checked)}
      />
      <Box sx={{ ml: 2 }}>
        <StyledLabel unrestricted>
          <ConfigFieldLabel
            fieldKey={fieldKey}
            label={GLOBAL_CONFIG_FIELD_LABELS[fieldKey]}
            dirtyFields={dirtyFields}
            tooltip={GLOBAL_CONFIG_FIELD_HELP[fieldKey]}
          />
        </StyledLabel>
        <StyledFieldDescription stacked>{description}</StyledFieldDescription>
      </Box>
    </StyledFieldRow>
  );
}

/**
 * Prefill with the coded default whenever no override is stored (null or "")
 * so the editor always shows what investors currently see. Editing and saving
 * persists it as an explicit override; clearing it falls back to the default.
 */
function GuardrailMessageField({ config, dirtyFields, onChange }: VeraResponsesSectionProps) {
  const storedOverride = config.veraGuardrailMessage;
  const guardrailValue =
    storedOverride && storedOverride.length > 0
      ? storedOverride
      : (config.veraGuardrailMessageDefault ?? '');

  return (
    <StyledFieldRow stacked sx={{ mb: 2 }} data-field="veraGuardrailMessage">
      <StyledLabel stacked>
        <ConfigFieldLabel
          fieldKey="veraGuardrailMessage"
          label={GLOBAL_CONFIG_FIELD_LABELS.veraGuardrailMessage}
          dirtyFields={dirtyFields}
          tooltip={GLOBAL_CONFIG_FIELD_HELP.veraGuardrailMessage}
        />
      </StyledLabel>
      <StyledFieldDescription stacked>
        The message investors see whenever Vera declines a request, in every Vera surface. Prefilled
        with the current default — edit and save to override it, or clear it to fall back to the
        default.
      </StyledFieldDescription>
      <GuardrailMessageEditor
        value={guardrailValue}
        onChange={(next) => onChange('veraGuardrailMessage', next)}
        placeholder={config.veraGuardrailMessageDefault ?? undefined}
      />
    </StyledFieldRow>
  );
}

function ThinkingBudgetField({ config, dirtyFields, onChange }: VeraResponsesSectionProps) {
  return (
    <StyledFieldRow stacked sx={{ mb: 2 }} data-field="veraThinkingBudget">
      <StyledLabel stacked>
        <ConfigFieldLabel
          fieldKey="veraThinkingBudget"
          label={GLOBAL_CONFIG_FIELD_LABELS.veraThinkingBudget}
          dirtyFields={dirtyFields}
          tooltip={GLOBAL_CONFIG_FIELD_HELP.veraThinkingBudget}
        />
      </StyledLabel>
      <StyledFieldDescription stacked>
        How much Gemini &quot;thinks&quot; before answering in Ask Vera. <strong>0</strong> = off
        (fastest, best for data lookups); <strong>-1</strong> = model decides;{' '}
        <strong>1–24576</strong> = fixed token budget (higher is slower).
      </StyledFieldDescription>
      <StyledInput
        type="number"
        value={config.veraThinkingBudget ?? 0}
        onChange={(e) => onChange('veraThinkingBudget', parseInt(e.target.value) ?? 0)}
        inputProps={{ min: -1, step: 1 }}
      />
    </StyledFieldRow>
  );
}

export default function VeraResponsesSection({
  config,
  dirtyFields,
  onChange,
}: VeraResponsesSectionProps) {
  return (
    <StyledSection tone="tone1">
      <StyledHeading tone="tone1" headerVariant="heading2">
        Responses &amp; Guardrails
      </StyledHeading>

      <GuardrailMessageField config={config} dirtyFields={dirtyFields} onChange={onChange} />

      <ThinkingBudgetField config={config} dirtyFields={dirtyFields} onChange={onChange} />

      <GroundingToggleRow
        fieldKey="enableVeraGoogleSearchGrounding"
        dirtyFields={dirtyFields}
        checked={!!config.enableVeraGoogleSearchGrounding}
        onChange={onChange}
        description="Lets the investor floating Vera chat use Google Search to ground answers. Admin assist has its own separate control."
      />

      <GroundingToggleRow
        fieldKey="enableVeraGlobalChatSearchLinks"
        dirtyFields={dirtyFields}
        checked={!!config.enableVeraGlobalChatSearchLinks}
        disabled={!config.enableVeraGoogleSearchGrounding}
        onChange={onChange}
        description="When off, investor global-chat replies come back without web source links even if grounding still runs. Admin assist is unaffected."
      />
    </StyledSection>
  );
}
